import { BinsoObject } from "@/types/parlor";

/**
 * 빈소 평형 추천 유틸 (실험 binsoOrderVariation B)
 *
 * 가설: 빈소 평형은 고객 언어가 아니라 첫 문항으로 두면 부담 -> 이탈.
 * 조문객 수를 먼저 받고, 그 값으로 평형을 추천(기본선택)해 인지부하를 줄인다.
 *
 * 추천 공식(팀원 현업 감각 기반 단순 휴리스틱):
 *   필요 평형 = 예상 조문객 수 x 0.44
 *   (피크 방문객 60% / 동시 체류 1/3 / 1인당 2.2평 을 합친 단일 계수)
 * "정답"이 아니라 안전 추천 기준이므로 계산값(평수 숫자)은 UI에 노출하지 않는다.
 */

/** 조문객 수 1명당 추천 평형 계수 (= 0.6 / 3 * 2.2) */
export const GUEST_TO_PYEONG = 0.44;

/** 빈소평형 선택지에서 추천 후보로 쓰는 옵션 (라벨 + 실제 평수) */
export interface BinsoOption {
  /** UI 버튼 라벨. value 매칭에 그대로 쓰이므로 빈소평형_LIST와 동일해야 함 */
  label: string;
  pyeong: number;
}

export interface BinsoRecommendation {
  /** 추천 옵션 라벨. 추천 불가(조문객 미상/무빈소/옵션 없음)면 null */
  label: string | null;
  /** 모든 옵션이 필요 평형보다 작음 (예: 200명 이상인데 큰 빈소 부재) -> 폴백 문구 노출 */
  isFallback: boolean;
  /** 옵션이 하나뿐 -> "추천"이 아니라 유일 옵션. 배지 제거 */
  isSingleOption: boolean;
}

/**
 * binsos 앞 5개에서 추천 후보 옵션을 만든다.
 * 기존 빈소평형_LIST(컴포넌트 인라인)와 라벨 규칙이 동일해야 value 매칭이 깨지지 않는다.
 * - index 4: `${pyeong}평 이상`
 * - index 0~3: `${pyeong}평`
 * - pyeong 없는 항목/중복 라벨 제외
 */
export function buildBinsoOptions(binsos: BinsoObject[]): BinsoOption[] {
  const seen = new Set<string>();
  const options: BinsoOption[] = [];

  binsos.forEach((binso, i) => {
    // 기존 인라인 빈소평형_LIST와 동일하게 truthy 체크 (pyeong=0/null 제외). 가격게이지 옵션 리스트와 정합 유지.
    if (i >= 5 || !binso.pyeong) return;
    const label = i === 4 ? `${binso.pyeong}평 이상` : `${binso.pyeong}평`;
    if (seen.has(label)) return;
    seen.add(label);
    options.push({ label, pyeong: binso.pyeong });
  });

  return options;
}

/**
 * 조문객 수 선택지에서 숫자만 추출.
 * "50명 내외" -> 50, "100명 내외" -> 100, "200명 이상" -> 200,
 * "0명(무빈소)" -> 0, "모르겠어요"/"없음" -> null
 */
function parseGuestCount(condolers: string): number | null {
  const matched = condolers.match(/(\d+)/);
  if (!matched) return null;
  return Number(matched[1]);
}

/**
 * 조문객 수 기준으로 가장 가까운(nearest) 빈소 평형을 추천한다.
 * ceil(이상 중 최소)은 옵션 간격이 듬성해 1/3이 16평+ 과추천 -> 신뢰 리스크라 nearest 채택.
 *
 * @param condolers 조문객수 선택값 (조문객예상인원_LIST 원소)
 * @param options buildBinsoOptions 결과
 */
export function recommendBinso(condolers: string, options: BinsoOption[]): BinsoRecommendation {
  const guestCount = parseGuestCount(condolers);

  // 조문객 미상("모르겠어요") / 무빈소(0명) / 옵션 없음 -> 추천 안 함
  if (guestCount == null || guestCount === 0 || options.length === 0) {
    return { label: null, isFallback: false, isSingleOption: false };
  }

  const targetPyeong = guestCount * GUEST_TO_PYEONG;

  // nearest: |pyeong - target| 최소
  const nearest = [...options].sort(
    (a, b) => Math.abs(a.pyeong - targetPyeong) - Math.abs(b.pyeong - targetPyeong),
  )[0];

  const maxPyeong = Math.max(...options.map((option) => option.pyeong));

  return {
    label: nearest.label,
    // 모든 옵션이 필요 평형 미달 -> nearest는 자동으로 가장 큰 옵션이 되고, 폴백 문구를 띄운다
    isFallback: maxPyeong < targetPyeong,
    isSingleOption: options.length === 1,
  };
}
